import { useEffect, useRef, useState } from "react";
import { publicAsset } from "../lib/hostConfig";
import type { DeviceTier } from "../scenario/types";

/**
 * Renders the root course library page designed in webdesign.darknetsearch.com
 * and exported as static HTML/CSS. Courses themselves open on their intro card.
 * The markup is mounted inside a Shadow DOM so its (fairly opinionated) CSS
 * can never leak into the sim3d app shell and vice versa. Interactivity
 * (quality/sound toggles, Start button, feature popups, waitlist form) is
 * re-wired here with plain DOM listeners scoped to the shadow root -- no
 * dependency on the original page's inline <script>.
 *
 * Source files live under `public/intro/` (fetched at runtime, not bundled):
 *   - intro/style.css            shared stylesheet for every intro page
 *   - intro/bodies/library.html  the library page
 *   - intro/img/<hash>.webp      optimized images referenced by both above
 * Both the CSS and HTML use the placeholder token `__INTROBASE__` in place
 * of the real asset base, resolved here via publicAsset("intro") so it works
 * whether the SPA is served standalone ("/intro/...") or under a different
 * Vite base in the future.
 */

export interface IntroPageCallbacks {
  onQualityChange?: (next: { tier: DeviceTier | null; reducedMotion: boolean | null }) => void;
  onSoundChange?: (on: boolean) => void;
  onStart?: () => void;
}

const QUALITY_KEY_MAP: Record<string, DeviceTier | null> = {
  auto: null,
  high: "high",
  balanced: "mid",
  low: "low",
};

const htmlCache = new Map<string, Promise<string | null>>();
let cssPromise: Promise<string> | null = null;

function fetchText(url: string): Promise<string | null> {
  return fetch(url)
    .then((r) => (r.ok ? r.text() : null))
    .catch(() => null);
}

function loadBodyHtml(bodyPath: string): Promise<string | null> {
  if (!htmlCache.has(bodyPath)) htmlCache.set(bodyPath, fetchText(publicAsset(bodyPath)));
  return htmlCache.get(bodyPath)!;
}

function loadCss(): Promise<string> {
  if (!cssPromise) cssPromise = fetchText(publicAsset("intro/style.css")).then((t) => t ?? "");
  return cssPromise;
}

export function IntroPage({
  bodyPath,
  callbacks,
  fallback,
  className,
}: {
  /** Path under public/, e.g. "intro/bodies/office-under-attack.html". */
  bodyPath: string;
  callbacks?: IntroPageCallbacks;
  /** Rendered instead of the shadow content if the fetch fails (offline/etc). */
  fallback?: React.ReactNode;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  // Listeners are wired ONCE per bodyPath mount, but the callbacks a parent
  // passes in can close over changing state (e.g. onStart reading the latest
  // soundOn). Route every call through this ref so it's never stale without
  // having to tear down and re-wire the shadow DOM on every parent render.
  const cbsRef = useRef<IntroPageCallbacks>(callbacks ?? {});
  useEffect(() => {
    cbsRef.current = callbacks ?? {};
  }, [callbacks]);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setState("loading");
    Promise.all([loadBodyHtml(bodyPath), loadCss()]).then(([html, css]) => {
      if (!alive) return;
      if (!html || !hostRef.current) {
        setState("failed");
        return;
      }
      const base = publicAsset("intro").replace(/\/$/, "");
      // @font-face is ignored inside a shadow root, so the page fonts load at document level.
      if (!document.getElementById("cf-intro-fonts")) {
        const link = document.createElement("link");
        link.id = "cf-intro-fonts";
        link.rel = "stylesheet";
        link.href = `${base}/fonts/fonts.css`;
        document.head.appendChild(link);
      }
      const root = hostRef.current.shadowRoot ?? hostRef.current.attachShadow({ mode: "open" });
      root.innerHTML =
        `<style>${css.split("__INTROBASE__").join(base)}</style>` +
        `<div class="cf-intro-doc">${html.split("__INTROBASE__").join(base)}</div>`;
      wireIntro(root, cbsRef, controller.signal);
      setState("ready");
    });
    return () => {
      alive = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyPath]);

  return (
    <div className={`cf-intro-shell ${className ?? ""}`}>
      <div ref={hostRef} className="cf-intro-host" />
      {state === "loading" && <div className="cf-intro-loading">Loading…</div>}
      {state === "failed" && fallback}
    </div>
  );
}

function wireIntro(root: ShadowRoot, cbsRef: { current: IntroPageCallbacks }, signal: AbortSignal) {
  const opts = { signal };
  const cbs = {
    get onQualityChange() {
      return cbsRef.current.onQualityChange;
    },
    get onSoundChange() {
      return cbsRef.current.onSoundChange;
    },
    get onStart() {
      return cbsRef.current.onStart;
    },
  };

  root.querySelectorAll<HTMLButtonElement>(".simulation-quality button[data-quality]").forEach((button) => {
    button.addEventListener(
      "click",
      () => {
        root.querySelectorAll<HTMLButtonElement>(".simulation-quality button").forEach((opt) => {
          const selected = opt === button;
          opt.classList.toggle("active", selected);
          opt.setAttribute("aria-pressed", selected ? "true" : "false");
        });
        const key = button.getAttribute("data-quality") ?? "auto";
        cbs.onQualityChange?.({ tier: QUALITY_KEY_MAP[key] ?? null, reducedMotion: null });
      },
      opts
    );
  });

  root.querySelectorAll<HTMLInputElement>(".simulation-motion input[type=checkbox]").forEach((input) => {
    input.addEventListener(
      "change",
      () => {
        const active = root.querySelector<HTMLButtonElement>(".simulation-quality button.active");
        const key = active?.getAttribute("data-quality") ?? "auto";
        cbs.onQualityChange?.({ tier: QUALITY_KEY_MAP[key] ?? null, reducedMotion: input.checked });
      },
      opts
    );
  });

  root.querySelectorAll<HTMLButtonElement>(".simulation-sound").forEach((button) => {
    button.addEventListener(
      "click",
      () => {
        const isOn = button.getAttribute("aria-pressed") !== "true";
        button.setAttribute("aria-pressed", isOn ? "true" : "false");
        button.innerHTML = `<span>${isOn ? "\u{1F50A}" : "\u{1F507}"}</span> Sound &amp; music: ${
          isOn ? "on" : "off"
        }`;
        cbs.onSoundChange?.(isOn);
      },
      opts
    );
  });

  root.querySelectorAll<HTMLAnchorElement>(".simulation-start").forEach((a) => {
    a.addEventListener(
      "click",
      (e) => {
        if (!cbs.onStart) return;
        e.preventDefault();
        cbs.onStart();
      },
      opts
    );
  });

  // Matches the exported page's own copy tweak: "Start course →" -> "Test Course".
  root.querySelectorAll<HTMLElement>(".course-meta").forEach((el) => {
    el.textContent = (el.textContent ?? "").replace(/\s*\u2192\s*$/, "").replace("Start course", "Test Course");
  });

  const lessonSearch = root.querySelector<HTMLInputElement>("#lesson-search");
  const lessonCount = root.querySelector<HTMLElement>(".lesson-count");
  const lessonEmpty = root.querySelector<HTMLElement>(".lesson-empty");
  if (lessonSearch) {
    const applyLessonFilter = () => {
      const q = lessonSearch.value.trim().toLowerCase();
      let shown = 0;
      root.querySelectorAll<HTMLElement>(".mission-board .course-card").forEach((card) => {
        const blob = (card.textContent || "").toLowerCase();
        const match = q.length === 0 || blob.includes(q);
        card.hidden = !match;
        if (match) shown += 1;
      });
      if (lessonCount) lessonCount.textContent = String(shown);
      if (lessonEmpty) lessonEmpty.hidden = shown !== 0;
    };
    lessonSearch.addEventListener("input", applyLessonFilter, opts);
  }

  root.querySelectorAll<HTMLButtonElement>(".feature-more").forEach((button) => {
    button.addEventListener(
      "click",
      () => {
        const id = button.getAttribute("data-popup");
        const popup = id ? root.getElementById(id) : null;
        if (popup) {
          popup.classList.add("open");
          popup.setAttribute("aria-hidden", "false");
        }
      },
      opts
    );
  });
  root.querySelectorAll<HTMLButtonElement>(".feature-popup-close").forEach((button) => {
    button.addEventListener(
      "click",
      () => {
        const popup = button.closest(".feature-popup");
        if (popup) {
          popup.classList.remove("open");
          popup.setAttribute("aria-hidden", "true");
        }
      },
      opts
    );
  });
  root.querySelectorAll<HTMLElement>(".feature-popup").forEach((popup) => {
    popup.addEventListener(
      "click",
      (e) => {
        if (e.target === popup) {
          popup.classList.remove("open");
          popup.setAttribute("aria-hidden", "true");
        }
      },
      opts
    );
  });
  root.addEventListener(
    "keydown",
    (e) => {
      if ((e as KeyboardEvent).key === "Escape") {
        root.querySelectorAll<HTMLElement>(".feature-popup.open").forEach((popup) => {
          popup.classList.remove("open");
          popup.setAttribute("aria-hidden", "true");
        });
      }
    },
    opts
  );

  const form = root.querySelector<HTMLFormElement>("#waitlistForm");
  if (form) {
    form.addEventListener(
      "submit",
      (e) => {
        e.preventDefault();
        const note = form.querySelector(".signup-note");
        if (note) note.textContent = "Thanks \u2014 we'll be in touch.";
        form.reset();
      },
      opts
    );
  }
}
